package com.mgacreative.hotelru.feature.maintenance.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.mgacreative.hotelru.shared.ui.designsystem.components.HFCard
import com.mgacreative.hotelru.shared.ui.designsystem.components.verticalScrollbar
import com.mgacreative.hotelru.shared.ui.designsystem.i18n.l10n
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MaintenanceListScreen(
    onNavigateToDetail: (String, String) -> Unit = { _, _ -> },
    onNavigateBack: () -> Unit = {},
    onNavigate: (String) -> Unit = {},
    viewModel: MaintenanceViewModel = koinViewModel()
) {
    val tasks by viewModel.tasks.collectAsState()
    val rooms by viewModel.rooms.collectAsState()

    var showAddDialog by remember { mutableStateOf(false) }

    androidx.compose.runtime.LaunchedEffect(Unit) {
        viewModel.loadData()
    }

    if (showAddDialog) {
        AddMaintenanceDialog(
            rooms = rooms,
            onDismiss = { showAddDialog = false },
            onAdd = { roomId, roomNo, cat, desc, priority ->
                viewModel.createTask(roomId, roomNo, cat, desc, priority)
                showAddDialog = false
            }
        )
    }

    Scaffold(
        topBar = {
            Surface(tonalElevation = 2.dp, shadowElevation = 2.dp) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(Color.White)
                        .padding(horizontal = 4.dp, vertical = 2.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = l10n("Back"))
                    }
                    Text(
                        text = l10n("Bakım & Onarım (Arızalar)"),
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                        color = Color(0xFF1E293B),
                        modifier = Modifier.weight(1f)
                    )
                    com.mgacreative.hotelru.shared.ui.designsystem.components.LanguageSelectorRow(
                        isDarkTheme = false,
                        modifier = Modifier.padding(end = 12.dp)
                    )
                }
            }
        },
        contentWindowInsets = WindowInsets(0, 0, 0, 0),
        floatingActionButton = {
            FloatingActionButton(
                onClick = { showAddDialog = true },
                containerColor = Color(0xFF2196F3),
                contentColor = Color.White
            ) {
                Icon(Icons.Default.Add, contentDescription = "Arıza Bildir")
            }
        },
        bottomBar = { MaintenanceBottomNavBar(onNavigate = onNavigate) }
    ) { paddingValues ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .background(Color(0xFFF1F5F9))
        ) {
            if (tasks.isEmpty()) {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Text(l10n("Aktif arıza kaydı bulunmuyor."), color = Color.Gray)
                }
            } else {
                val listState = androidx.compose.foundation.lazy.rememberLazyListState()
                LazyColumn(
                    state = listState,
                    modifier = Modifier.fillMaxSize().verticalScrollbar(listState),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    items(tasks) { task ->
                        MaintenanceCard(
                            task = task,
                            onClick = { onNavigateToDetail(task.id, task.roomId) },
                            onClose = { viewModel.closeTask(task.id, task.roomId) }
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun MaintenanceCard(task: MaintenanceTask, onClick: () -> Unit, onClose: () -> Unit) {
    val priorityColor = when (task.priority) {
        "ACİL" -> Color(0xFFEF4444)
        "NORMAL" -> Color(0xFFF59E0B)
        else -> Color(0xFF10B981)
    }
    
    val statusColor = when (task.status) {
        "AÇIK" -> Color(0xFFEF4444)
        "DEVAM_EDIYOR" -> Color(0xFFF59E0B)
        else -> Color(0xFF10B981)
    }

    HFCard(modifier = Modifier.fillMaxWidth().clickable { onClick() }) {
        Row(modifier = Modifier.fillMaxWidth().height(IntrinsicSize.Min)) {
            // Priority Indicator
            Box(modifier = Modifier.width(4.dp).fillMaxHeight().background(priorityColor))
            
            Column(modifier = Modifier.fillMaxWidth().padding(16.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Warning, contentDescription = null, tint = priorityColor, modifier = Modifier.size(20.dp))
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Oda ${task.roomNumber}", fontWeight = FontWeight.Bold, fontSize = 16.sp, color = Color(0xFF1E293B))
                    }
                    
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(task.status, color = statusColor, fontWeight = FontWeight.Medium, fontSize = 12.sp)
                        Spacer(Modifier.width(8.dp))
                        IconButton(onClick = onClose, modifier = Modifier.size(24.dp)) {
                            Icon(Icons.Default.CheckCircle, contentDescription = "Kapat", tint = Color(0xFF10B981))
                        }
                    }
                }
                
                Spacer(modifier = Modifier.height(8.dp))
                
                Text(task.description, fontSize = 14.sp, color = Color(0xFF475569))
                
                Spacer(modifier = Modifier.height(12.dp))
                
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Category, contentDescription = null, tint = Color.Gray, modifier = Modifier.size(14.dp))
                        Spacer(modifier = Modifier.width(4.dp))
                        Text(task.category, color = Color.Gray, fontSize = 12.sp)
                    }
                    Text(task.createdAt, color = Color.Gray, fontSize = 12.sp)
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddMaintenanceDialog(
    rooms: List<com.mgacreative.hotelru.domain.model.Room>,
    onDismiss: () -> Unit,
    onAdd: (roomId: String, roomNo: String, category: String, desc: String, priority: String) -> Unit
) {
    var selectedRoom by remember { mutableStateOf<com.mgacreative.hotelru.domain.model.Room?>(null) }
    var roomExpanded by remember { mutableStateOf(false) }
    
    var category by remember { mutableStateOf("Genel") }
    val categories = listOf("Genel", "Klima", "Tesisat", "Elektrik", "Mobilya", "TV/İnternet", "Diğer")
    var catExpanded by remember { mutableStateOf(false) }
    
    var priority by remember { mutableStateOf("NORMAL") }
    val priorities = listOf("DÜŞÜK", "NORMAL", "ACİL")
    
    var description by remember { mutableStateOf("") }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = Color.White,
        shape = RoundedCornerShape(20.dp),
        title = { Text(l10n("Yeni Arıza Bildir")) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                // Room Selection
                Box {
                    OutlinedTextField(
                        value = selectedRoom?.roomNumber ?: l10n("Oda Seçin"),
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(l10n("Oda")) },
                        trailingIcon = { Icon(Icons.Default.ArrowDropDown, null) },
                        modifier = Modifier.fillMaxWidth()
                    )
                    Box(modifier = Modifier.matchParentSize().clickable { roomExpanded = true })
                    DropdownMenu(expanded = roomExpanded, onDismissRequest = { roomExpanded = false }) {
                        rooms.forEach { room ->
                            DropdownMenuItem(
                                text = { Text("Oda ${room.roomNumber} (${room.roomType})") },
                                onClick = { selectedRoom = room; roomExpanded = false }
                            )
                        }
                    }
                }

                // Category Selection
                Box {
                    OutlinedTextField(
                        value = l10n(category),
                        onValueChange = {},
                        readOnly = true,
                        label = { Text(l10n("Kategori")) },
                        trailingIcon = { Icon(Icons.Default.ArrowDropDown, null) },
                        modifier = Modifier.fillMaxWidth()
                    )
                    Box(modifier = Modifier.matchParentSize().clickable { catExpanded = true })
                    DropdownMenu(expanded = catExpanded, onDismissRequest = { catExpanded = false }) {
                        categories.forEach { cat ->
                            DropdownMenuItem(
                                text = { Text(l10n(cat)) },
                                onClick = { category = cat; catExpanded = false }
                            )
                        }
                    }
                }

                // Priority Selection
                Column {
                    Text(l10n("Öncelik"), fontSize = 12.sp, color = Color.Gray)
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        priorities.forEach { p ->
                            FilterChip(
                                selected = priority == p,
                                onClick = { priority = p },
                                label = { Text(l10n(p)) }
                            )
                        }
                    }
                }

                OutlinedTextField(
                    value = description,
                    onValueChange = { description = it },
                    label = { Text(l10n("Arıza Açıklaması")) },
                    modifier = Modifier.fillMaxWidth(),
                    minLines = 3
                )
            }
        },
        confirmButton = {
            Button(
                onClick = { 
                    selectedRoom?.let { room ->
                        onAdd(room.id ?: "", room.roomNumber, category, description, priority)
                    }
                },
                enabled = selectedRoom != null && description.isNotBlank(),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF2196F3))
            ) {
                Text(l10n("Kaydet"))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text(l10n("İptal")) }
        }
    )
}

@Composable
fun MaintenanceTopBar() {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(Color(0xFF2196F3))
            .padding(horizontal = 16.dp, vertical = 16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(Icons.Default.Menu, contentDescription = "Menu", tint = Color.White)
        Spacer(modifier = Modifier.width(16.dp))
        Text(
            text = l10n("Bakım & Onarım (Arızalar)"),
            color = Color.White,
            fontSize = 20.sp,
            fontWeight = FontWeight.Medium,
            modifier = Modifier.weight(1f)
        )
        Icon(Icons.Default.Build, contentDescription = l10n("Arızalar"), tint = Color.White)
    }
}

@Composable
fun MaintenanceBottomNavBar(onNavigate: (String) -> Unit = {}) {
    NavigationBar(
        containerColor = Color.White,
        contentColor = Color.Gray
    ) {
        NavigationBarItem(
            icon = { Icon(Icons.Default.Build, contentDescription = l10n("Arızalar")) },
            label = { Text(l10n("Arızalar"), fontSize = 10.sp) },
            selected = true,
            colors = NavigationBarItemDefaults.colors(
                selectedIconColor = Color(0xFF2196F3),
                selectedTextColor = Color(0xFF2196F3),
                indicatorColor = Color(0xFFE3F2FD)
            ),
            onClick = { }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.CheckCircle, contentDescription = l10n("Çözülenler")) },
            label = { Text(l10n("Çözülenler"), fontSize = 10.sp) },
            selected = false,
            onClick = { }
        )
        NavigationBarItem(
            icon = { Icon(Icons.Default.ShoppingCart, contentDescription = l10n("Parçalar")) },
            label = { Text(l10n("Parçalar"), fontSize = 10.sp) },
            selected = false,
            onClick = { onNavigate("InventoryScreen") }
        )
    }
}

